import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import bot from '../../../Assets/patrick.png';
import paperclip from '../../../Assets/paperclip.svg';
import santa from '../../../Assets/santa.png';
import { JWT } from "../../../Utils/JWT";
import Constants from '../../../Constants';
import { isWinterHolidayRange } from '../../Shared/WinterStyle';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

interface Message {
  sender: string;
  content: string;
  time: string;
}

interface Messages extends Array<Message> { }

interface RenderMessagesProps {
  messages: Messages;
  handleButtonClick: () => void;
  isOnline: boolean;
  first_name: string;
  last_name: string;
  isAwaitingResponse: boolean;
}

const isValidMessage = (message: Message): boolean => {
  return message.sender !== 'assistant_fail' &&
    !(message.content.includes('busy : true') && /\((de|nl|fr|es|en)\)/i.test(message.content));
};

const findValidMessage = (messages: Messages, index: number, direction: 'previous' | 'next'): Message | null => {
  let currentIndex = direction === 'previous' ? index - 1 : index + 1;

  while (currentIndex >= 0 && currentIndex < messages.length) {
    const currentMessage = messages[currentIndex];
    if (isValidMessage(currentMessage)) {
      return currentMessage;
    }
    currentIndex += direction === 'previous' ? -1 : 1;
  }

  return null;
};

const cleanMessageContent = (content: string, sender: string): string => {
  const languageRegex = /\((\w{2})\)(?=[^\w]*$)/;

  if (sender === 'assistant') {
    return content
      .replace(languageRegex, '')
      .replace(/\s+upload\s*:\s*true/gi, '')
      .replace(/\s+upload\s*:\s*false/gi, '')
      .trim();
  }

  return content.replace(languageRegex, '').trim();
};

const extractLanguage = (content: string, defaultLanguage: string): string => {
  const languageRegex = /\((\w{2})\)(?=[^\w]*$)/;
  const match = content.match(languageRegex);
  return match ? match[1].toLowerCase() : defaultLanguage;
};

const MarkdownParser: React.FC<{ content: string }> = React.memo(({ content }) => {
  const parseContent = useMemo(() => {
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`|~~[^~]+~~|``[^`]+``|```[^`]+```)/g;
    const tokens = content.split(regex);

    const parseToken = (token: string, index: number): React.ReactNode => {
      if (token.startsWith('**') && token.endsWith('**')) {
        return <b key={index}>{token.slice(2, -2)}</b>;
      } else if ((token.startsWith('*') && token.endsWith('*')) || (token.startsWith('_') && token.endsWith('_'))) {
        return <i key={index}>{token.slice(1, -1)}</i>;
      } else if (token.startsWith('```') && token.endsWith('```')) {
        return <code className='large-code' key={index}>{token.slice(4, -3)}</code>;
      } else if (token.startsWith('``') && token.endsWith('``')) {
        return <code className='large-code' key={index}>{token.slice(2, -2)}</code>;
      } else if (token.startsWith('`') && token.endsWith('`')) {
        return <code className='normal-code' key={index}>{token.slice(1, -1)}</code>;
      } else if (token.startsWith('~~') && token.endsWith('~~')) {
        return <del key={index}>{token.slice(2, -2)}</del>;
      }
      return token;
    };

    return tokens.map(parseToken);
  }, [content]);

  return <>{parseContent}</>;
});

// Patrick avatar
const Avatar: React.FC<{ className?: string }> = React.memo(({ className = '' }) => (
  <div className={`relative rounded-full bg-[#e3e2dc] mr-1.5 mt-[9px] w-[35px] h-[35px] overflow-hidden ${className}`}>
    <img src={isWinterHolidayRange ? santa : bot} alt="Patrick assistance" className="absolute top-0 left-0 w-[70px] mt-[4px]" />
  </div>
));

// User avatar
const UserAvatar: React.FC<{ firstName: string; lastName: string; t: any }> = React.memo(({ firstName, lastName, t }) => (
  <div className="w-9 h-9 ml-1.5 mt-2 bg-[#3273f6] text-white flex items-center justify-center text-xs rounded-full">
    {(firstName && lastName) ? firstName[0] + lastName[0] : t("patrick:you")}
  </div>
));

// Error avatar
const ErrorAvatar: React.FC<{ className?: string }> = React.memo(({ className = '' }) => (
  <div className={`relative rounded-full bg-[#e3e2dc] mr-1.5 mt-[9px] w-[35px] h-[35px] overflow-hidden flex items-center justify-center ${className}`}>
    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 w-5 h-5" />
  </div>
));

// Message bubble
const MessageBubble: React.FC<{
  sender: string;
  children: React.ReactNode;
  className?: string
}> = React.memo(({ sender, children, className = '' }) => (
  <div className={`inline-block p-4 ${sender === 'assistant' ? 'border' : 'bg-[#F5F8FB]'} border-[#D9E3F1] rounded-[25px] text-[#5D6C78] text-[14px] break-words max-w-[190px] md:max-w-[270px] whitespace-pre-wrap ${className}`}>
    {children}
  </div>
));

// Support mail link
const SupportEmail: React.FC = React.memo(() => (
  <a className='text-[#3273f6] hover:underline' href="mailto: support@digipas.app">
    support@digipas.app
  </a>
));

// Special message handlers
const SpecialMessageHandlers = {
  upload: (isNewSender: boolean, t: any, msg: any, messageLanguage: string, isSocialUser: boolean, isOnline: boolean, isLastInSequence: boolean) => (
    <>
      <div className={`flex justify-start ${!isNewSender ? 'pl-10' : ''}`}>
        {isNewSender && <Avatar />}
      </div>
      <UploadButton
        messageContent={msg.content}
        messageLanguage={messageLanguage}
        isSocialUser={isSocialUser}
        isOnline={isOnline}
        t={t}
      />
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  ),

  offline: (isNewSender: boolean, t: any, i18n: any, msg: any, isLastInSequence: boolean) => (
    <>
      <div className={`flex justify-start ${!isNewSender ? 'pl-10' : ''}`}>
        {isNewSender && <Avatar />}
        <MessageBubble sender="assistant">
          {t("patrick:offlinePrompt", { lng: i18n.language })}{' '}
            <SupportEmail />.
        </MessageBubble>
      </div>
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  ),

  buttons: (isNewSender: boolean, messageContent: string, messageLanguage: string, isSocialUser: boolean, t: any, handleButtonClick: () => void, msg: any, isLastInSequence: boolean) => (
    <>
      <div className={`flex justify-start ${!isNewSender ? 'pl-10' : ''}`}>
        {isNewSender && <Avatar />}
        {!isSocialUser ? (
          <MessageBubble sender="assistant">
            {t("patrick:importPrompt", { lng: messageLanguage })}
            <button
              className="bg-[#3273f6] text-white rounded-[10px] px-3 py-2 text-xs w-[100%] my-1 disabled:bg-[#7e7e7e]"
              onClick={handleButtonClick}
              disabled={messageContent === 'relocate : true'}
            >
              {t('patrick:mapping', { lng: messageLanguage })}
            </button>
          </MessageBubble>
        ) : (
          <MessageBubble sender="assistant">
            {t("patrick:notAllowed", { lng: messageLanguage })}
          </MessageBubble>
        )}
      </div>
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  ),

  download: (isNewSender: boolean, messageLanguage: string, t: any, msg: any, isLastInSequence: boolean) => (
    <>
      <div className={`flex justify-start ${!isNewSender ? 'pl-10' : ''}`}>
        {isNewSender && <Avatar />}
        <MessageBubble sender="assistant">
          {t("patrick:downloadPrompt", { lng: messageLanguage })}{' '}
          <a
            className='my-2 text-[#3273f6] hover:underline'
            href={Constants.DOWNLOAD_URL_CARDS_TEMPLATE}
            download
          >
            {t("patrick:downloadPromptSecondary", { lng: messageLanguage })}
          </a>.
        </MessageBubble>
      </div>
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  ),

  busy: (messageContent: string, t: any, msg: any, isLastInSequence: boolean) => (
    <>
      <div className="mt-1.5 mb-1.5">
        <div className="flex justify-start mt-2.5">
          <Avatar />
          <div className={`inline-block p-4 border border-[#D9E3F1] rounded-[25px] text-[#5D6C78] break-words h-[26px] whitespace-pre-wrap ${[".csv", ".xls", ".xlsx"].some(ext => messageContent.toLowerCase().includes(ext)) ? 'busy' : 'loader'}`}>
            <p className='opacity-0'>
              {[".csv", ".xls", ".xlsx"].some(ext => messageContent.toLowerCase().includes(ext))
                ? t("patrick:busy")
                : t("patrick:typingLoader")
              }...
            </p>
          </div>
        </div>
      </div>
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  ),

  error: (isNewSender: boolean, t: any, i18n: any, msg: any, isLastInSequence: boolean) => (
    <>
      <div className={`flex justify-start ${!isNewSender ? 'pl-10' : ''}`}>
        {isNewSender && <ErrorAvatar />}
        <MessageBubble sender="assistant">
          {t("patrick:error", { lng: i18n.language })}{' '}
          <SupportEmail />.
        </MessageBubble>
      </div>
      {isLastInSequence && (
        <div className="text-[#B6BFC7] flex justify-start pl-16 text-xs mb-2.5">
          {msg.time}
        </div>
      )}
    </>
  )
};

const UploadButton: React.FC<{
  messageContent: string;
  messageLanguage: string;
  isSocialUser: boolean;
  isOnline: boolean;
  t: any
}> = React.memo(({ messageContent, messageLanguage, isSocialUser, isOnline, t }) => {
  if (!messageContent.toLowerCase().includes("upload : true") && !messageContent.includes("upload : false")) {
    return null;
  }

  return (
    <div className="flex justify-start mt-1.5">
      <div className='pl-10'></div>
      {!isSocialUser ? (
        <div className="max-w-[400px] p-[6px] pl-[16px] border border-[#D9E3F1] rounded-[25px] text-[#5D6C78] text-[14px] flex flex-row items-center gap-[5px]">
          {t("patrick:fileUploadPrompt", { lng: messageLanguage })}
          <label htmlFor={isOnline ? 'csvFileInput' : undefined} className={isOnline ? 'cursor-pointer' : 'cursor-not-allowed'}>
            <div className={`${isOnline && 'shake'} max-w-[400px] bg-[#F5F8FB] rounded-[25px] text-[#5D6C78] flex flex-row items-center gap-[5px] p-[10px]`}>
              <img src={paperclip} alt="File upload" className="shake-item w-2.5 h-4" />
              {t("patrick:upload", { lng: messageLanguage })}
            </div>
          </label>
        </div>
      ) : (
        <MessageBubble sender="assistant">
          {t("patrick:notAllowed", { lng: messageLanguage })}
        </MessageBubble>
      )}
    </div>
  );
});

const MessageItem: React.FC<{
  msg: Message;
  index: number;
  messages: Messages;
  isOnline: boolean;
  firstName: string;
  lastName: string;
  isAwaitingResponse: boolean;
  handleButtonClick: () => void;
  isSocialUser: boolean;
  t: any;
  i18n: any;
}> = React.memo(({
  msg,
  index,
  messages,
  isOnline,
  firstName,
  lastName,
  isAwaitingResponse,
  handleButtonClick,
  isSocialUser,
  t,
  i18n
}) => {
  if (!messages || !Array.isArray(messages)) {
    return null;
  }

  const prevMessage = useMemo(() => findValidMessage(messages, index, 'previous'), [messages, index]);
  const nextMessage = useMemo(() => findValidMessage(messages, index, 'next'), [messages, index]);

  const isNewSender = !prevMessage || prevMessage.sender !== msg.sender;
  const isLastInSequence = !nextMessage || nextMessage.sender !== msg.sender;

  const messageLanguage = useMemo(() => extractLanguage(msg.content, i18n.language.toLowerCase()), [msg.content, i18n.language]);
  const messageContent = useMemo(() => cleanMessageContent(msg.content, msg.sender), [msg.content, msg.sender]);

  // Skip invalid messages
  if (msg.sender === 'assistant_fail') return (
    SpecialMessageHandlers.error(isNewSender, t, i18n, msg, isLastInSequence)
  );

  // Special message handlers
  const specialMessageMap: Record<string, () => React.ReactNode> = {
    'upload : true': () => SpecialMessageHandlers.upload(isNewSender, t, msg, messageLanguage, isSocialUser, isOnline, isLastInSequence),
    'upload : false': () => SpecialMessageHandlers.upload(isNewSender, t, msg, messageLanguage, isSocialUser, isOnline, isLastInSequence),
    'offline : true': () => SpecialMessageHandlers.offline(isNewSender, t, i18n, msg, isLastInSequence),
    'gotobuttons : true': () => SpecialMessageHandlers.buttons(isNewSender, messageContent, messageLanguage, isSocialUser, t, handleButtonClick, msg, isLastInSequence),
    'relocate : true': () => SpecialMessageHandlers.buttons(isNewSender, messageContent, messageLanguage, isSocialUser, t, handleButtonClick, msg, isLastInSequence),
    'download : true': () => SpecialMessageHandlers.download(isNewSender, messageLanguage, t, msg, isLastInSequence),
    'busy : true': () => isLastInSequence ? SpecialMessageHandlers.busy(msg.content, t, msg, isLastInSequence) : null
  };

  // Handle special messages
  if (msg.sender === 'assistant' && specialMessageMap[messageContent]) {
    return <>{specialMessageMap[messageContent]()}</>;
  }

  // Normal message
  const renderMessageContent = () => {
    if (msg.sender === 'assistant') {
      if (messageContent === 'start message') {
        return isOnline
          ? t("patrick:startPrompt", { lng: i18n.language })
          : <>
            {t("patrick:offlinePrompt", { lng: i18n.language })}
            <a className='text-[#3273f6] hover:underline' href="mailto: support@digipas.app">
              support@digipas.app
            </a>.
          </>;
      }
      return <MarkdownParser content={messageContent} />;
    }
    return messageContent;
  };

  return (
    <>
      <div key={`${msg.time}-${index}`} className={msg.sender === 'assistant' && (messageContent === 'busy : true') && !isLastInSequence ? '' : 'mb-1.5'}>
        <div className={`flex ${msg.sender === 'assistant' ? 'justify-start' : 'justify-end'} ${msg.sender === 'assistant' && !isNewSender ? 'pl-10' : ''} ${msg.sender === 'human' && !isNewSender ? 'pr-11' : ''}`}>
          {isNewSender && msg.sender === 'assistant' && <Avatar />}

          <MessageBubble sender={msg.sender}>
            {renderMessageContent()}
            {msg.sender === 'assistant' && msg.content.toLowerCase().includes("upload : true") &&
              "\n\n" + t("patrick:importingSuggestionPrompt", { lng: messageLanguage })
            }
          </MessageBubble>

          {msg.sender === 'human' && isNewSender && (
            <UserAvatar firstName={firstName} lastName={lastName} t={t} />
          )}
        </div>

        <UploadButton
          messageContent={msg.content}
          messageLanguage={messageLanguage}
          isSocialUser={isSocialUser}
          isOnline={isOnline}
          t={t}
        />

        {/* Time indicator */}
        {isLastInSequence && (
          <div className={`text-[#B6BFC7] flex ${msg.sender === 'assistant' ? 'justify-start pl-16' : 'justify-end pr-16'} text-xs mb-2.5`}>
            {msg.time}
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {isLastInSequence && !nextMessage && isAwaitingResponse && (
        <div key={`${msg.time}-${index}-load`} className="mb-1.5">
          <div className="flex justify-start mt-2.5">
            <Avatar />
            <div className={`inline-block p-4 border border-[#D9E3F1] rounded-[25px] text-[#5D6C78] break-words h-[26px] whitespace-pre-wrap ${[".csv", ".xls", ".xlsx"].some(ext => msg.content.toLowerCase().includes(ext)) ? 'busy' : 'loader'}`}>
              <p className='opacity-0'>
                {[".csv", ".xls", ".xlsx"].some(ext => msg.content.toLowerCase().includes(ext))
                  ? t("patrick:busy")
                  : t("patrick:typingLoader")
                }...
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

const RenderMessages: React.FC<RenderMessagesProps> = ({
  messages,
  handleButtonClick,
  isOnline,
  first_name,
  last_name,
  isAwaitingResponse
}) => {
  const { t, i18n } = useTranslation();

  if (!messages || !Array.isArray(messages)) {
    return null;
  }

  const isSocialUser = useMemo(() =>
    Boolean(JWT.read<Array<string>>('roles')?.includes('ROLE_SOCIAL')), []
  );

  const memoizedHandleButtonClick = useCallback(() => {
    handleButtonClick();
  }, [handleButtonClick]);

  return (
    <>
      {messages.map((msg, index) => (
        <MessageItem
          key={`${msg.time}-${index}`}
          msg={msg}
          index={index}
          messages={messages}
          isOnline={isOnline}
          firstName={first_name}
          lastName={last_name}
          isAwaitingResponse={isAwaitingResponse}
          handleButtonClick={memoizedHandleButtonClick}
          isSocialUser={isSocialUser}
          t={t}
          i18n={i18n}
        />
      ))}
    </>
  );
};

export default RenderMessages;
